import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const auth = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Employee invitations (roadmap item #17 Phase 1).
// `lookup` and `accept` are token-gated public endpoints — they
// deliberately don't require an existing session, so the invitee can use
// them before they have an account.
export const employeeInvitations = {
  list: () => api.get('/admin/employee-invitations'),
  create: (data) => api.post('/admin/employee-invitations', data),
  revoke: (id) => api.delete(`/admin/employee-invitations/${id}`),
  resend: (id) => api.post(`/admin/employee-invitations/${id}/resend`),
  lookup: (token) => api.get(`/employee-invitations/${token}`),
  accept: (token, data) => api.post(`/employee-invitations/${token}/accept`, data),
};

export const pets = {
  getAll: () => api.get('/pets'),
  getOne: (id) => api.get(`/pets/${id}`),
  create: (data) => api.post('/pets', data),
  update: (id, data) => api.put(`/pets/${id}`, data),
  delete: (id) => api.delete(`/pets/${id}`),
  uploadImage: (id, file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post(`/pets/${id}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadDocument: (id, file, documentName, documentType) => {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentName', documentName);
    formData.append('documentType', documentType);
    return api.post(`/pets/${id}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const appointments = {
  getAll: () => api.get('/appointments'),
  getOne: (id) => api.get(`/appointments/${id}`),
  create: (data) => api.post('/appointments', data),
  cancel: (id) => api.delete(`/appointments/${id}`),
  getAvailableSlots: (params) => api.get('/appointments/available-slots', { params }),
  getCapacity: (date) => api.get(`/appointments/capacity/${date}`),
  applyDiscount: (id, data) => api.patch(`/appointments/${id}/discount`, data),
  removeDiscount: (id) => api.delete(`/appointments/${id}/discount`),
  applyRefund: (id, data) => api.patch(`/appointments/${id}/refund`, data),
  removeRefund: (id) => api.delete(`/appointments/${id}/refund`),
  processPayment: (id, data) => api.patch(`/appointments/${id}/payment`, data),
  reassignGroomer: (id, groomerId) => api.patch(`/appointments/${id}/reassign-groomer`, { groomerId }),
};

export const services = {
  getAll: (params) => api.get('/services', { params }),
  getOne: (id) => api.get(`/services/${id}`),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
};

export const groomers = {
  getAll: () => api.get('/groomers'),
  getMe: () => api.get('/groomers/me'),
  getAvailability: (id, date) => api.get(`/groomers/${id}/availability/${date}`),
};

export const admin = {
  getDashboard: () => api.get('/admin/dashboard'),
  getAppointments: (params) => api.get('/admin/appointments', { params }),
  updateAppointmentStatus: (id, data) => api.patch(`/appointments/${id}/status`, data),
  getClients: () => api.get('/admin/clients'),
  getClientDetails: (id) => api.get(`/admin/clients/${id}`),
  updateClient: (id, data) => api.put(`/admin/clients/${id}`, data),
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (settings) => api.put('/admin/settings', { settings }),
  getAllUsers: () => api.get('/admin/users'),
  getUserDetails: (userId) => api.get(`/admin/users/${userId}`),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role`, { role }),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  mergeUsers: (sourceUserId, targetUserId) => api.post('/admin/users/merge', { sourceUserId, targetUserId }),
  createAdmin: (data) => api.post('/admin/users/create-admin', data),
  createGroomer: (data) => api.post('/admin/users/create-groomer', data),
  importClients: (clients) => api.post('/admin/import-clients', { clients }),
  archiveAppointments: () => api.post('/admin/archive-appointments'),
  getArchiveStatus: () => api.get('/admin/archive-status'),
  getSystemInfo: () => api.get('/admin/system-info'),
  runMigrations: () => api.post('/admin/run-migrations'),
  exportConfig: () => api.get('/admin/export-config', { responseType: 'blob' }),
};

export const groomerCapacity = {
  getDefaults: (groomerId) => api.get(`/groomer-capacity/defaults/${groomerId}`),
  setDefault: (data) => api.post('/groomer-capacity/defaults', data),
  deleteDefault: (id) => api.delete(`/groomer-capacity/defaults/${id}`),
  getOverrides: (groomerId, params) => api.get(`/groomer-capacity/overrides/${groomerId}`, { params }),
  setOverride: (data) => api.post('/groomer-capacity/overrides', data),
  deleteOverride: (id) => api.delete(`/groomer-capacity/overrides/${id}`),
  checkCapacity: (groomerId, date, size, serviceId) => 
    api.get('/groomer-capacity/check', { params: { groomerId, date, size, serviceId } }),
};

export const timeOff = {
  getAll: (params) => api.get('/time-off', { params }),
  getCalendar: (params) => api.get('/time-off/calendar', { params }),
  getById: (id) => api.get(`/time-off/${id}`),
  create: (data) => api.post('/time-off', data),
  update: (id, data) => api.put(`/time-off/${id}`, data),
  review: (id, data) => api.patch(`/time-off/${id}/review`, data),
  delete: (id) => api.delete(`/time-off/${id}`),
  checkAvailability: (data) => api.post('/time-off/check-availability', data),
};

export const users = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  changePassword: (data) => api.put('/users/profile/password', data),
  deleteAccount: (password) => api.delete('/users/profile', { data: { password } }),
};

export const addons = {
  getAll: (params) => api.get('/addons', { params }),
  getOne: (id) => api.get(`/addons/${id}`),
  create: (data) => api.post('/addons', data),
  update: (id, data) => api.put(`/addons/${id}`, data),
  delete: (id) => api.delete(`/addons/${id}`),
  toggle: (id) => api.patch(`/addons/${id}/toggle`),
};

export const recommendations = {
  getAll: () => api.get('/recommendations'),
  getByCategory: (category) => api.get(`/recommendations/category/${category}`),
  getOne: (id) => api.get(`/recommendations/${id}`),
  create: (data) => api.post('/recommendations', data),
  update: (id, data) => api.put(`/recommendations/${id}`, data),
  delete: (id) => api.delete(`/recommendations/${id}`),
  getCategories: () => api.get('/recommendations/categories/all'),
  createCategory: (data) => api.post('/recommendations/categories', data),
  updateCategory: (id, data) => api.put(`/recommendations/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/recommendations/categories/${id}`),
};

export const stats = {
  getEarnings: (period) => api.get(`/stats/earnings?period=${period}`),
  getAllTime: () => api.get('/stats/all-time'),
  getTopClients: (limit = 10) => api.get(`/stats/top-clients?limit=${limit}`),
  getInventory: () => api.get('/stats/inventory'),
};

export const inventory = {
  getAll: (params) => api.get('/inventory', { params }),
  getOne: (id) => api.get(`/inventory/${id}`),
  create: (data) => api.post('/inventory', data),
  update: (id, data) => api.put(`/inventory/${id}`, data),
  delete: (id) => api.delete(`/inventory/${id}`),
  getLowStock: () => api.get('/inventory/alerts/low-stock'),
};

export const clientDocuments = {
  list: (clientId) => api.get(`/clients/${clientId}/documents`),
  upload: (clientId, file, { documentType, label, requestId } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (documentType) formData.append('documentType', documentType);
    if (label) formData.append('label', label);
    if (requestId) formData.append('requestId', requestId);
    return api.post(`/clients/${clientId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (clientId, docId) => api.delete(`/clients/${clientId}/documents/${docId}`),
  approve: (clientId, docId) => api.post(`/clients/${clientId}/documents/${docId}/approve`),
  getSettings: () => api.get('/client-documents/settings'),
  updateSettings: (data) => api.put('/client-documents/settings', data),
};

export const clientIntakeTemplate = {
  list: () => api.get('/client-intake-template'),
  listPublic: () => api.get('/client-intake-template/public'),
  create: (data) => api.post('/client-intake-template', data),
  update: (id, data) => api.put(`/client-intake-template/${id}`, data),
  delete: (id) => api.delete(`/client-intake-template/${id}`),
};

export const clientDocumentRequests = {
  list: (clientId, params = {}) => api.get(`/clients/${clientId}/document-requests`, { params }),
  create: (clientId, data) => api.post(`/clients/${clientId}/document-requests`, data),
  update: (clientId, reqId, data) => api.put(`/clients/${clientId}/document-requests/${reqId}`, data),
  waive: (clientId, reqId) => api.post(`/clients/${clientId}/document-requests/${reqId}/waive`),
  delete: (clientId, reqId) => api.delete(`/clients/${clientId}/document-requests/${reqId}`),
};

// Minimal helper for the form-picker UI in document-request /
// intake-template editors. Full forms management still lives in
// FormsManager.js with direct axios calls; this only needs list.
export const forms = {
  list: () => api.get('/forms'),
};

// Roadmap item #18: calendar annotations (informational notes/events
// pinned to specific dates — distinct from shop closures which gate
// booking).
export const calendarAnnotations = {
  list: () => api.get('/calendar-annotations'),
  range: (startDate, endDate) => api.get(`/calendar-annotations/range/${startDate}/${endDate}`),
  create: (data) => api.post('/calendar-annotations', data),
  update: (id, data) => api.put(`/calendar-annotations/${id}`, data),
  delete: (id) => api.delete(`/calendar-annotations/${id}`),
};

// Roadmap item #20: guest sign-in / no-account booking. The /config
// endpoint is public (the public booking page hits it before login)
// and returns enabled flag + required fields + service list.
export const guestBooking = {
  config: () => api.get('/guest-booking/config'),
  submit: (data) => api.post('/guest-booking', data),
  updateSettings: (data) => api.put('/admin/guest-booking/settings', data),
  // Phase 2: admin management of the guest_clients table.
  listGuests: () => api.get('/admin/guest-clients'),
  promote: (guestId) => api.post(`/admin/guest-clients/${guestId}/promote`),
  // Phase 2c: mint a public form-fill share token for one of the
  // configured required intake forms. Used by the guest booking page
  // before final submit.
  spawnFormSubmission: (formId) => api.post('/guest-booking/spawn-form-submission', { form_id: formId }),
  // Phase 2c (docs): upload one required document. Multipart; the
  // server validates the label is in the configured required list.
  uploadDocument: (label, file) => {
    const fd = new FormData();
    fd.append('label', label);
    fd.append('file', file);
    return api.post('/guest-booking/upload-document', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Roadmap item #19 Phase 1: recurring appointments. All endpoints are
// admin-or-groomer; groomers only get write access when the
// `employees_can_create_recurring_appointments` system setting is on
// (the server enforces this; we just call whichever the user gets).
export const appointmentSeries = {
  list: (status) => api.get('/appointment-series', { params: status ? { status } : {} }),
  get: (id) => api.get(`/appointment-series/${id}`),
  create: (data) => api.post('/appointment-series', data),
  update: (id, data) => api.put(`/appointment-series/${id}`, data),
  pause: (id) => api.post(`/appointment-series/${id}/pause`),
  resume: (id) => api.post(`/appointment-series/${id}/resume`),
  end: (id, cancelFuture = false) => api.post(`/appointment-series/${id}/end`, { cancel_future_appointments: cancelFuture }),
  materializeOne: (id) => api.post(`/appointment-series/${id}/materialize`),
  runAll: () => api.post('/appointment-series/run'),
};

// Roadmap item #19 Phase 2: recurring reminders. Same perms model as
// appointmentSeries — gated by `employees_can_create_recurring_reminders`
// for non-admin groomers.
export const recurringReminders = {
  list: (params) => api.get('/recurring-reminders', { params }),
  get: (id) => api.get(`/recurring-reminders/${id}`),
  create: (data) => api.post('/recurring-reminders', data),
  update: (id, data) => api.put(`/recurring-reminders/${id}`, data),
  pause: (id) => api.post(`/recurring-reminders/${id}/pause`),
  resume: (id) => api.post(`/recurring-reminders/${id}/resume`),
  end: (id) => api.post(`/recurring-reminders/${id}/end`),
  sendNow: (id) => api.post(`/recurring-reminders/${id}/send-now`),
  runAll: () => api.post('/recurring-reminders/run'),
};

// Roadmap item #21: membership IDs. Settings are read/written via the
// generic admin.getSettings/updateSettings endpoints; these helpers
// cover the action endpoints (preview, assign/regenerate, backfill).
export const membershipIds = {
  preview: () => api.get('/admin/membership-ids/preview'),
  assign: (clientId, body = {}) => api.post(`/admin/clients/${clientId}/membership-id`, body),
  clear: (clientId) => api.delete(`/admin/clients/${clientId}/membership-id`),
  backfill: () => api.post('/admin/membership-ids/backfill'),
};

export const sizeCategories = {
  getAll: () => api.get('/size-categories'),
  getOne: (id) => api.get(`/size-categories/${id}`),
  create: (data) => api.post('/size-categories', data),
  update: (id, data) => api.put(`/size-categories/${id}`, data),
  delete: (id) => api.delete(`/size-categories/${id}`),
  updateOrder: (categories) => api.put('/size-categories/order', { categories }),
};

export default api;
